import { useState, useEffect, useCallback, useRef, type DependencyList } from 'react';

export interface UseAsyncResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
  /** Re-run the fetcher with the current deps. Used by retry buttons. */
  refetch: () => void;
}

/**
 * Generic data-fetch hook for the "load once, retry on failure, refetch on
 * dep change" pattern. Replaces the four bespoke `apiClient.getX().then(set).
 * catch(console.error)` blocks that all silently swallowed errors and shared
 * no retry mechanism.
 *
 * Stale-response guard: every fetch increments a request id; responses from
 * superseded requests are dropped. Old data stays visible during a refetch
 * (no flash of empty state).
 *
 * The fetcher is captured in a ref so its identity is irrelevant — only `deps`
 * and `refetch` trigger re-runs. Callers don't need to memoize the fetcher.
 */
export function useAsync<T>(fetcher: () => Promise<T>, deps: DependencyList): UseAsyncResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const requestIdRef = useRef(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    fetcherRef.current()
      .then(d => {
        if (requestId !== requestIdRef.current) return;
        setData(d);
      })
      .catch((err: unknown) => {
        if (requestId !== requestIdRef.current) return;
        console.error('useAsync fetch failed:', err);
        setError(err instanceof Error ? err.message : 'Failed');
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsLoading(false);
      });
    // The deps array is the caller's contract: we re-fetch when any of these
    // change. Adding the fetcher would defeat the ref-based identity skip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, error, isLoading, refetch };
}
